/**
 * App.IO - 保存/导出/导入 + 自动暂存
 *
 * 第一部分：序列化/反序列化和导出功能
 */

import type { ExportScene, ExportFloor } from '../types';
import { TileType } from '../types';
import { App } from './App';

/* ─────────────── Module: IO（保存/导出/导入 + 自动暂存）─────────────── */
App.IO = {
  /** localStorage 键名 */
  KEY: 'mep-routing-autosave-v3',

  /** 数据格式标识 */
  SCHEMA: 'mep-routing-v2',

  /** 版本号 */
  VER: 3,

  /** 自动保存定时器 */
  saveTimer: null as ReturnType<typeof setTimeout> | null,

  // ════════════════════════════════════════════════════════════════════
  // 序列化
  // ════════════════════════════════════════════════════════════════════

  /**
   * 序列化当前场景为纯 JSON 对象（Map→对象，Set→数组）；含全部楼层
   */
  serializeScene(): ExportScene {
    const S = App.Store;

    const floors = S.floors.map(f => ({
      id: f.id,
      name: f.name,
      yBase: f.yBase,
      type: f.type,
      rooms: f.rooms.map(r => ({
        id: r.id,
        x: r.x,
        z: r.z,
        color: r.color,
        shape: r.shape,
        added: !!r.added,
        floorIdx: r.floorIdx,
        systemId: r.systemId || 0,
      })),
      ends: f.ends.map(e => ({
        id: e.id,
        x: e.x,
        z: e.z,
        color: e.color,
        added: !!e.added,
        floorIdx: e.floorIdx,
        systemId: e.systemId || 0,
      })),
      lines: f.lines.map(l => ({
        vertices: l.vertices.map(v => ({ x: v.x, z: v.z })),
        cells: l.cells.map(c => ({ x: c.x, z: c.z })),
      })),
      bindings: this._map2obj(f.bindings, v => [...v]),
      endOrig: this._map2obj(f.endOrig),
      roomOrig: this._map2obj(f.roomOrig),
      drawnOrig: this._map2obj(f.drawnOrig),
      riserOrig: this._map2obj(f.riserOrig),
    }));

    return {
      schema: this.SCHEMA,
      version: this.VER,
      grid: { ...App.Config.GRID },
      layoutIdx: S.layoutIdx,
      bindMode: S.bindMode,
      activeFloorIdx: S.activeFloorIdx,
      floorCounter: S.floorCounter,
      roomCounter: S.roomCounter,
      endCounter: S.endCounter,
      shaftCounter: S.shaftCounter,
      systemCounter: S.systemCounter,
      cornerPenalty: S.cornerPenalty,
      floors,
      shafts: S.shafts.map(s => ({ id: s.id, x: s.x, z: s.z, floorFrom: s.floorFrom, floorTo: s.floorTo, systemId: s.systemId || 0 })),
      systems: S.systems.map(s => ({ id: s.id, name: s.name, color: s.color, abbr: s.abbr || '' })),
    };
  },

  /**
   * Map 转普通对象
   */
  _map2obj<T>(m: Map<string, T>, valFn?: (v: T) => any): Record<string, any> {
    const o: Record<string, any> = {};
    for (const [k, v] of m) {
      o[k] = valFn ? valFn(v) : v;
    }
    return o;
  },

  // ════════════════════════════════════════════════════════════════════
  // 反序列化
  // ════════════════════════════════════════════════════════════════════

  /**
   * 反序列化 JSON → 重建 Store（全部楼层）
   */
  applyScene(snap: ExportScene): void {
    const S = App.Store;

    if (!snap || !snap.floors || !snap.floors.length) {
      throw new Error('场景数据无效');
    }

    S.floors = snap.floors.map((sf, fi) => {
      const f = {
        id: sf.id || ('F' + (fi + 1)),
        name: sf.name || App.Config.FLOOR_NAMES[fi] || ('第' + (fi + 1) + '层'),
        yBase: sf.yBase != null ? sf.yBase : fi * App.Config.FLOOR_HEIGHT,
        type: sf.type,
        rooms: sf.rooms || [],
        ends: sf.ends || [],
        lines: sf.lines || [],
        drawnCells: new Set<string>(),
        drawnOrig: this._obj2map(sf.drawnOrig),
        endOrig: this._obj2map(sf.endOrig),
        roomOrig: this._obj2map(sf.roomOrig),
        riserOrig: this._obj2map(sf.riserOrig),
        junctions: [],
        bindings: this._obj2map(sf.bindings, v => new Set(v)),
      };

      // 确保对象结构完整
      f.rooms.forEach((r: any) => {
        if (!r.shape) r.shape = App.Config.ROOM_SHAPE_MAIN;
        r.floorIdx = r.floorIdx != null ? r.floorIdx : fi;
        r.systemId = r.systemId != null ? r.systemId : 0;
      });

      f.ends.forEach((e: any) => {
        e.floorIdx = e.floorIdx != null ? e.floorIdx : fi;
        e.systemId = e.systemId != null ? e.systemId : 0;
      });

      f.lines.forEach((l: any) => {
        if (!l.cells && l.vertices) {
          l.cells = (App.World as any).rasterizeVertices(l.vertices);
        }
      });

      // 重算交接点
      f.junctions = [];
      const cnt = new Map<string, number>();
      for (const line of f.lines) {
        const local = new Set(line.cells.map((c: any) => c.x + ',' + c.z));
        for (const k of local) {
          cnt.set(k, (cnt.get(k) || 0) + 1);
        }
      }
      for (const [k, n] of cnt) {
        if (n >= 2) {
          const [x, z] = k.split(',').map(Number);
          f.junctions.push({ x, z });
        }
      }

      return f;
    });

    S.activeFloorIdx = Math.min(snap.activeFloorIdx || 0, S.floors.length - 1);
    S.f = S.floors[S.activeFloorIdx];

    S.floorCounter = snap.floorCounter || S.floors.length;
    S.layoutIdx = snap.layoutIdx || 0;
    S.bindMode = !!snap.bindMode;
    S.roomCounter = snap.roomCounter || 1;
    S.endCounter = snap.endCounter || 4;
    S.curLine = [];
    S.results = [];

    // 竖井注册表：优先用快照中的 shafts；否则从网格 RISER 格子重建（兼容旧格式）
    const W = App.World as any;
    if (Array.isArray((snap as any).shafts) && (snap as any).shafts.length) {
      S.shafts = (snap as any).shafts.map((s: any) => ({
        id: s.id,
        x: s.x,
        z: s.z,
        floorFrom: Math.max(0, s.floorFrom | 0),
        floorTo: Math.min(S.floors.length - 1, s.floorTo | 0),
        systemId: s.systemId != null ? s.systemId : 0,
      }));
      S.shaftCounter = (snap as any).shaftCounter || S.shafts.reduce((m: number, s: any) => Math.max(m, s.id), 0);
      W.reapplyShafts();
    } else {
      S.shafts = [];
      S.shaftCounter = 0;
      this._rebuildShaftsFromGrid();
    }

    const ln = document.getElementById('layoutName');
    if (ln) ln.textContent = App.Config.LAYOUTS[S.layoutIdx];

    const cb = document.getElementById('chkBind');
    if (cb) (cb as HTMLInputElement).checked = S.bindMode;

    // 管线系统注册表：优先用快照中的 systems；否则用默认预设（兼容旧格式）
    if (Array.isArray((snap as any).systems) && (snap as any).systems.length) {
      S.systems = (snap as any).systems.map((s: any) => ({ id: s.id, name: s.name, color: s.color, abbr: s.abbr || '' }));
      S.systemCounter = (snap as any).systemCounter || S.systems.reduce((m: number, s: any) => Math.max(m, s.id), 0);
    }
    // activeSystemId 容错
    if (!S.systems.some((s: any) => s.id === S.activeSystemId)) {
      S.activeSystemId = S.systems[0] ? S.systems[0].id : 0;
    }

    // 折弯惩罚
    S.cornerPenalty = (snap as any).cornerPenalty || 0;
    const cp = document.getElementById('cornerPenalty');
    if (cp) (cp as HTMLInputElement).value = String(S.cornerPenalty);
  },

  /**
   * 从网格的 RISER 格子重建竖井注册表（兼容无 shafts 字段的旧导出）
   */
  _rebuildShaftsFromGrid(): void {
    const S = App.Store;
    const T = App.Config.T;
    const map = new Map<string, number[]>();
    S.floors.forEach((f, fi) => {
      for (let x = 0; x < f.type.length; x++) {
        for (let z = 0; z < f.type[x].length; z++) {
          if (f.type[x][z] === T.RISER) {
            const k = x + ',' + z;
            if (!map.has(k)) map.set(k, []);
            map.get(k)!.push(fi);
          }
        }
      }
    });
    for (const [k, fis] of map) {
      const [x, z] = k.split(',').map(Number);
      S.shafts.push({
        id: ++S.shaftCounter,
        x,
        z,
        floorFrom: Math.min(...fis),
        floorTo: Math.max(...fis),
        systemId: 0,
      });
    }
  },

  /**
   * 普通对象转 Map
   */
  _obj2map<T>(o: Record<string, any> | undefined, valFn?: (v: any) => T): Map<string, T> {
    const m = new Map<string, T>();
    if (!o) return m;
    for (const k in o) {
      m.set(k, valFn ? valFn(o[k]) : o[k]);
    }
    return m;
  },

  // ════════════════════════════════════════════════════════════════════
  // V1 格式迁移
  // ════════════════════════════════════════════════════════════════════

  /**
   * 检测旧格式（V1 / 无 schema）
   */
  looksLikeV1(snap: any): boolean {
    return !snap.schema && Array.isArray(
      snap.floors ? (snap.floors[0] && snap.floors[0].type) : snap.type
    );
  },

  /**
   * V1 迁移到 V2/V3 格式
   * V1：{type[x][z], rooms, ends, lines, ...} 单层 → 包成 v2 floors
   */
  migrateV1(snap: any): ExportScene {
    const v1type = snap.floors ? snap.floors[0].type : snap.type;

    return {
      schema: this.SCHEMA,
      version: this.VER,
      grid: { ...App.Config.GRID },
      layoutIdx: 0,
      bindMode: false,
      activeFloorIdx: 0,
      floorCounter: 1,
      roomCounter: (snap.rooms && snap.rooms.length) || 1,
      endCounter: (snap.ends && snap.ends.length) || 4,
      shaftCounter: 0,
      systemCounter: 0,
      cornerPenalty: 0,
      shafts: [],
      systems: [],
      floors: [
        {
          id: 'F1',
          name: '一层',
          yBase: 0,
          type: v1type,
          rooms: (snap.rooms || []).map((r: any) => ({
            id: r.id,
            x: r.x,
            z: r.z,
            color: r.color,
            shape: r.shape || App.Config.ROOM_SHAPE_MAIN,
            added: !!r.added,
            floorIdx: 0,
          })),
          ends: (snap.ends || []).map((e: any) => ({
            id: e.id,
            x: e.x,
            z: e.z,
            color: e.color,
            added: !!e.added,
            floorIdx: 0,
          })),
          lines: (snap.lines || []).map((l: any) => ({
            vertices: (l.vertices || []).map((v: any) => ({ x: v.x, z: v.z })),
            cells: (l.cells || []).map((c: any) => ({ x: c.x, z: c.z })),
          })),
          bindings: {},
          endOrig: snap.endOrig || {},
          roomOrig: snap.roomOrig || {},
          drawnOrig: snap.drawnOrig || {},
          riserOrig: {},
        },
      ],
    };
  },

  // ════════════════════════════════════════════════════════════════════
  // 导出
  // ════════════════════════════════════════════════════════════════════

  /**
   * 导出 JSON
   */
  exportJSON(): void {
    const snap = this.serializeScene();
    this._download(
      'mep-scene-' + this._stamp() + '.json',
      JSON.stringify(snap, null, 2),
      'application/json'
    );

    let objs = 0;
    for (const f of snap.floors) {
      objs += f.rooms.length + f.ends.length + f.lines.length;
    }
    App.UI.setProgress('已导出场景 JSON · ' + snap.floors.length + ' 层 · ' + objs + ' 个对象');
  },

  /**
   * 导出 CSV
   */
  exportCSV(): void {
    const S = App.Store;
    const res = S.results;

    if (!res.length) {
      App.UI.setProgress('请先生成连接路径再导出 CSV');
      return;
    }

    const rows: string[][] = [
      ['末端ID', '楼层', '连接机房', '路径长度(m)', '折点数', '状态', '备注'],
    ];

    for (const r of res) {
      const f = S.floors[r.end.floorIdx];
      rows.push([
        r.end.id,
        f ? f.name : '—',
        r.from || '—',
        r.ok ? r.len.toString() : '0',
        r.ok ? r.cor.toString() : '0',
        r.ok ? '连通' : r.reason === '未绑定机房' ? '未绑定' : '隔离',
        r.ok ? (r.end.floorIdx !== 0 ? '跨层' : '同层') : '',
      ]);
    }

    const csv = rows
      .map(row => row.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(','))
      .join('\r\n');

    this._download('mep-routing-' + this._stamp() + '.csv', '﻿' + csv, 'text/csv;charset=utf-8');
    App.UI.setProgress('已导出路径明细 CSV（' + res.length + ' 条）');
  },

  /**
   * 导入 JSON
   */
  async importJSON(file: File): Promise<void> {
    let snap: ExportScene | any;

    try {
      snap = JSON.parse(await file.text());
    } catch (e: any) {
      alert('文件不是有效 JSON：' + e.message);
      return;
    }

    if (snap.schema !== this.SCHEMA) {
      if (this.looksLikeV1(snap)) {
        snap = this.migrateV1(snap);
      } else {
        alert('未知的场景格式');
        return;
      }
    }

    try {
      this.applyScene(snap);
    } catch (e: any) {
      alert('场景重建失败：' + e.message);
      return;
    }

    (App.Command as any).clear();

    const RR = App.Render;
    const UI = App.UI;
    const S = App.Store;

    RR.buildGrid();
    RR.rebuildAll();
    RR.gPath.clear();
    RR.clearFlows();
    RR.gSearch.clear();
    RR.fitCamera();

    UI.refreshMetrics(0);
    UI.updateDrawStatus();
    UI.renderBindUI();
    UI.renderFloorPanel();
    UI.updatePlanBadge();

    (App.Selection as any).clear();

    let rooms = 0, ends = 0, lines = 0;
    for (const f of S.floors) {
      rooms += f.rooms.length;
      ends += f.ends.length;
      lines += f.lines.length;
    }

    UI.setProgress('已导入场景 · ' + S.floors.length + ' 层 · 机房 ' + rooms + ' · 末端 ' + ends + ' · 走管线 ' + lines);
  },

  // ════════════════════════════════════════════════════════════════════
  // localStorage 自动暂存
  // ════════════════════════════════════════════════════════════════════

  /**
   * 安排自动保存
   */
  scheduleAutoSave(): void {
    clearTimeout(this.saveTimer!);
    this.saveTimer = setTimeout(() => {
      try {
        localStorage.setItem(this.KEY, JSON.stringify(this.serializeScene()));
      } catch (e) {
        // 静默失败
      }
    }, 800);
  },

  /**
   * 加载自动保存
   */
  loadAutoSave(): ExportScene | null {
    try {
      const raw = localStorage.getItem(this.KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  },

  /**
   * 清除自动保存
   */
  clearAutoSave(): void {
    try {
      localStorage.removeItem(this.KEY);
    } catch (e) {
      // 静默失败
    }
  },

  // ════════════════════════════════════════════════════════════════════
  // 工具函数
  // ════════════════════════════════════════════════════════════════════

  /**
   * 生成时间戳字符串
   */
  _stamp(): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate()) + '-' + p(d.getHours()) + p(d.getMinutes()) + p(d.getSeconds());
  },

  /**
   * 触发下载
   */
  _download(name: string, content: string, mime: string): void {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  },
};

export default App.IO;
export const IO = App.IO;
